import * as recurrence from 'rrule'
import { z } from 'zod'

const { RRule } = ('default' in recurrence ? recurrence.default : recurrence) as typeof recurrence

export const recurringPaymentTypeSchema = z.enum(['invoice', 'donation'])
export const recurringDonationUrlSchema = z.string().url().refine((value) => new URL(value).protocol === 'https:', 'Use an https donation link')

export const bookingDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}, 'Enter a valid date')

export const bookingTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Enter a time in HH:MM format')

export function recurringEndLimit(startDate: string, today = new Date().toISOString().slice(0, 10)): string {
  const addYear = (value: string) => {
    const date = new Date(`${value}T00:00:00Z`)
    const month = date.getUTCMonth()
    date.setUTCFullYear(date.getUTCFullYear() + 1)
    if (date.getUTCMonth() !== month) date.setUTCDate(0)
    return date.toISOString().slice(0, 10)
  }
  return [addYear(startDate), addYear(today)].sort()[0]!
}

export const recurringScheduleSchema = z.object({
  patternType: z.enum(['weekly', 'monthly_day', 'monthly_week']),
  dayValue: z.number().int().min(0).max(31).optional(),
  dayOfWeek: z.number().int().min(0).max(6).optional(),
  weekOfMonth: z.number().int().min(1).max(5).optional(),
  weekInterval: z.number().int().min(1).max(12).default(1),
  startTime: bookingTimeSchema,
  endTime: bookingTimeSchema,
  startDate: bookingDateSchema,
  endDate: bookingDateSchema,
}).superRefine((value, context) => {
  const issue = (field: string, message: string) => context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message })
  if (value.endTime <= value.startTime) issue('endTime', 'End time must be after start time')
  if (value.endDate < value.startDate) issue('endDate', 'End date must not be before the first date')
  if (bookingDateSchema.safeParse(value.startDate).success && value.endDate > recurringEndLimit(value.startDate)) {
    issue('endDate', 'Recurring bookings can run for no more than 12 months and cannot be booked more than 12 months ahead')
  }
  if (value.patternType === 'weekly') {
    const weekday = value.dayValue ?? value.dayOfWeek
    if (weekday == null || weekday > 6) issue('dayValue', 'Choose a weekday')
  }
  if (value.patternType === 'monthly_day' && (!value.dayValue || value.dayValue > 31)) issue('dayValue', 'Choose a day from 1 to 31')
  if (value.patternType === 'monthly_week' && (value.dayOfWeek == null || value.weekOfMonth == null)) issue('weekOfMonth', 'Choose a weekday and which week of the month')
})

export interface RecurringPattern {
  patternType: string
  dayOfWeek: number | null
  dayOfMonth: number | null
  weekOfMonth: number | null
  weekInterval: number | null
  startDate: string
  endDate: string | null
}

export function recurringDateTime(date: string, time: string, timeZone: string): Date {
  const [instant] = new RRule({ freq: RRule.DAILY, count: 1, dtstart: new Date(`${date}T${time}:00Z`), tzid: timeZone }).all()
  if (!instant) throw new Error('Invalid booking time')
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(instant)
  if (parts !== time) throw new Error('This local time is unavailable because the clocks change. Choose a different time.')
  return instant
}

export function computeRecurringDates(pattern: RecurringPattern, rangeStart: string, rangeEnd: string, exceptions = new Set<string>()): string[] {
  const start = [pattern.startDate, rangeStart].sort().at(-1)!
  const end = [pattern.endDate || rangeEnd, rangeEnd].sort()[0]!
  if (start > end) return []
  const weekdays = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA]
  const weekday = pattern.dayOfWeek == null ? undefined : weekdays[pattern.dayOfWeek]
  if ((pattern.patternType === 'weekly' || pattern.patternType === 'monthly_week') && !weekday) return []
  const anchor = new Date(`${pattern.startDate}T00:00:00Z`)
  if (pattern.patternType === 'weekly' && pattern.dayOfWeek != null) {
    anchor.setUTCDate(anchor.getUTCDate() + (pattern.dayOfWeek - anchor.getUTCDay() + 7) % 7)
  }
  const rule = new RRule({
    dtstart: anchor,
    until: new Date(`${end}T23:59:59Z`),
    freq: pattern.patternType === 'weekly' ? RRule.WEEKLY : RRule.MONTHLY,
    interval: pattern.patternType === 'weekly' ? pattern.weekInterval || 1 : 1,
    ...(pattern.patternType === 'weekly' ? { byweekday: weekday } : {}),
    ...(pattern.patternType === 'monthly_week' ? { byweekday: weekday?.nth(pattern.weekOfMonth === 5 ? -1 : pattern.weekOfMonth || 1) } : {}),
    ...(pattern.patternType === 'monthly_day' ? { bymonthday: [pattern.dayOfMonth || 1, -1], bysetpos: 1 } : {}),
  })
  return rule.between(new Date(`${start}T00:00:00Z`), new Date(`${end}T23:59:59Z`), true)
    .map((date) => date.toISOString().slice(0, 10)).filter((date) => !exceptions.has(date))
}

export function describeRecurrence(pattern: RecurringPattern): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  if (pattern.patternType === 'weekly') {
    const interval = pattern.weekInterval || 1
    return interval === 1 ? `Every ${days[pattern.dayOfWeek ?? 0]}` : `Every ${interval} weeks on ${days[pattern.dayOfWeek ?? 0]}`
  }
  if (pattern.patternType === 'monthly_day') return `Day ${pattern.dayOfMonth} of each month`
  return `${['First', 'Second', 'Third', 'Fourth', 'Last'][(pattern.weekOfMonth || 1) - 1]} ${days[pattern.dayOfWeek ?? 0]} of each month`
}